/**
 * Cerrar la última pestaña del portal cierra la sesión (ADR-0030).
 *
 * Para saber si la que se cierra es la última, cada pestaña se anota en
 * `localStorage` —lo comparten todas las del sitio— con la última vez que dijo
 * «sigo aquí», y se borra al irse. Si al irse no queda ninguna otra, avisa al
 * servidor, que espera un momento: recargar también es «irse», y la página que
 * carga después lo desmiente.
 */

/** Cada cuánto dice una pestaña que sigue abierta. */
export const LATIDO_DE_PESTANA_MS = 15_000;

/**
 * Cuánto sin latir para darla por cerrada. Una pestaña en segundo plano la frena
 * el navegador a un latido por minuto: con dos minutos no se confunde con una
 * cerrada, y una que se cayó sin despedirse no estorba para siempre.
 */
export const VIGENCIA_DE_PESTANA_MS = 2 * 60_000;

const CLAVE = 'cpsp:pestanas';

/** Lo que hace falta de `localStorage`: así se prueba sin navegador. */
export type Almacen = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * El identificador de **esta carga** de la página. Viaja en cada petición: el
 * servidor distingue así una página que volvió de una petición rezagada de la
 * que se fue. Recargar da otro; volver de la caché del navegador, también.
 */
let idDeLaPagina = crypto.randomUUID();
export const idDeEstaPagina = () => idDeLaPagina;
export function renovarIdDeLaPagina(): void {
  idDeLaPagina = crypto.randomUUID();
}

function leer(almacen: Almacen): Record<string, number> | null {
  try {
    const valor = JSON.parse(almacen.getItem(CLAVE) ?? '{}') as unknown;
    return valor && typeof valor === 'object' ? (valor as Record<string, number>) : {};
  } catch {
    return null;
  }
}

function escribir(almacen: Almacen, registro: Record<string, number>): void {
  try {
    almacen.setItem(CLAVE, JSON.stringify(registro));
  } catch {
    // Sin almacenamiento no se sabe de las demás: ver `despedirse`.
  }
}

const vivas = (registro: Record<string, number>, ahora: number) =>
  Object.fromEntries(Object.entries(registro).filter(([, vez]) => ahora - vez < VIGENCIA_DE_PESTANA_MS));

/** «Sigo aquí»: se anota, y de paso olvida las que se cayeron sin despedirse. */
export function latir(almacen: Almacen, pestana: string, ahora: number): void {
  const registro = leer(almacen);
  if (!registro) return;
  escribir(almacen, { ...vivas(registro, ahora), [pestana]: ahora });
}

/**
 * Se borra y dice si **queda otra** abierta. Si no se puede saber —el navegador
 * no deja usar el almacenamiento—, dice que sí: es preferible que la sesión
 * venza por inactividad a sacar a alguien que tiene otra pestaña abierta.
 */
export function despedirse(almacen: Almacen, pestana: string, ahora: number): boolean {
  const registro = leer(almacen);
  if (!registro) return true;
  const restantes = vivas(registro, ahora);
  delete restantes[pestana];
  escribir(almacen, restantes);
  return Object.keys(restantes).length > 0;
}

/**
 * Salir a propósito —a cambiar la contraseña en Keycloak, que regresa al
 * portal— no es cerrar: durante un momento, irse no avisa.
 */
let aPropositoHasta = 0;
export function saldraAProposito(ahora: number): void {
  aPropositoHasta = ahora + 10_000;
}
export const esSalidaAProposito = (ahora: number) => ahora < aPropositoHasta;
